import type { AxiosInstance } from 'axios';

const CLASS_NAME_SHOW = 'show';

interface CaptchaToken {
    sessionKey: string;
    value: string;
}

interface FormTokenResponse {
    captchas?: {
        duplicate?: CaptchaToken;
        javascript?: CaptchaToken;
    };
    csrf: {
        input: string;
    };
}

interface FormieController {
    formTheme?: {
        updateFormHash: () => void;
    };
}

type FormieForm = HTMLFormElement & {
    form?: FormieController;
};

const loadAxios = async (): Promise<AxiosInstance> => {
    const { default: axios } = await import('axios');

    return axios;
};

const getTarget = (toggle: Element): HTMLElement | null => {
    const selector = toggle.getAttribute('data-target');

    return selector ? document.querySelector<HTMLElement>(selector) : null;
};

const isShown = (element: Element): boolean => element.classList.contains(CLASS_NAME_SHOW);

const resetDropdowns = (navItemToggles: NodeListOf<HTMLElement>): void => {
    navItemToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    document.querySelectorAll<HTMLElement>('.subnav').forEach((subnav) => {
        subnav.classList.remove(CLASS_NAME_SHOW);
    });
};

const handleAriaAndCollapsedClass = (
    toggler: Element,
    collapse: HTMLElement,
    isOpen: boolean,
): void => {
    toggler.setAttribute('aria-expanded', String(!isOpen));
    collapse.classList.toggle(CLASS_NAME_SHOW, !isOpen);
};

const updateCaptchaToken = (form: HTMLFormElement, token: CaptchaToken): void => {
    const input = form.querySelector<HTMLInputElement>(`input[name="${token.sessionKey}"]`);

    if (input) {
        input.value = token.value;
    }
};

const refreshFormTokens = async (forms: NodeListOf<FormieForm>): Promise<void> => {
    const request = await loadAxios();

    forms.forEach((form) => {
        const csrfInput = form.querySelector<HTMLInputElement>('input[name="CRAFT_CSRF_TOKEN"]');
        const formHandle = form.querySelector<HTMLInputElement>('input[name="handle"]')?.value;

        if (!csrfInput || !formHandle) {
            return;
        }

        request
            .get<FormTokenResponse>(`/actions/formie/forms/refresh-tokens?form=${formHandle}`)
            .then(({ data }) => {
                csrfInput.outerHTML = data.csrf.input;

                if (data.captchas?.javascript) {
                    updateCaptchaToken(form, data.captchas.javascript);
                }

                if (data.captchas?.duplicate) {
                    updateCaptchaToken(form, data.captchas.duplicate);
                }

                form.form?.formTheme?.updateFormHash();
            })
            .catch((error: unknown) => console.error(error));
    });
};

document.addEventListener('DOMContentLoaded', () => {
    const navToggle = document.querySelector<HTMLElement>('.nav-toggle');
    const navItemToggles = document.querySelectorAll<HTMLElement>('.nav__dropdown');

    navToggle?.addEventListener('click', () => {
        const collapseNav = getTarget(navToggle);

        if (!collapseNav) {
            return;
        }

        resetDropdowns(navItemToggles);
        handleAriaAndCollapsedClass(navToggle, collapseNav, isShown(collapseNav));
    });

    navItemToggles.forEach((navItemToggle) => {
        navItemToggle.addEventListener('click', () => {
            const dropdownSubnav = getTarget(navItemToggle);

            if (!dropdownSubnav) {
                return;
            }

            navItemToggles.forEach((otherToggle) => {
                if (otherToggle === navItemToggle) {
                    return;
                }

                otherToggle.setAttribute('aria-expanded', 'false');
                getTarget(otherToggle)?.classList.remove(CLASS_NAME_SHOW);
            });

            handleAriaAndCollapsedClass(navItemToggle, dropdownSubnav, isShown(dropdownSubnav));
        });
    });

    document.querySelectorAll<HTMLButtonElement>('.accordion-toggle button').forEach((toggle) => {
        toggle.addEventListener('click', () => {
            const accordion = toggle.parentElement?.parentElement;

            if (accordion) {
                handleAriaAndCollapsedClass(toggle, accordion, isShown(accordion));
            }
        });
    });

    if (document.querySelector('.lazyframe')) {
        void import('./utils/lazyframe-wrapper');
    }

    if (document.querySelector('.image-rotator')) {
        void import('./utils/swiper-wrapper');
    }

    const formieForms = document.querySelectorAll<FormieForm>('form');

    if (formieForms.length > 0) {
        void refreshFormTokens(formieForms).catch(() => {
            console.error('Unable to refresh form tokens');
        });
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log('HMR');
    });
}
